/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { DetailStatStrip } from '@/shared/ui';
import {
  accrualPeriodOf,
  findExpectation,
  findMonthlyRentCents,
  formatAccrualPeriod,
  formatCents,
  formatIsoDate,
  previousAccrualPeriod,
  resolveSituation,
} from '../utils/lease-detail-derivations';
import type { Lease, LeaseExpectation } from '../types/lease.types';

/** The tone a KPI value is painted in: the theme's status text colours. */
const TONE_COLOR = {
  good: 'var(--status-success-text)',
  warn: 'var(--status-warning-text)',
  bad: 'var(--status-error-text)',
  neutral: 'var(--text-primary)',
} as const;

type Tone = keyof typeof TONE_COLOR;

interface Kpi {
  /** Inline beside the value: the amount, when there is one. */
  secondary?: string;
  id: string;
  label: string;
  value: string;
  hint: string;
  tone: Tone;
}

/** The shape this row needs from a vistoria; the full type lives in its own feature. */
export interface EntryVistoria {
  type?: string;
  status: string;
  photos?: unknown[];
}

export interface LeaseDetailKpiCardsProps {
  lease: Lease;
  expectations: LeaseExpectation[];
  vistorias: EntryVistoria[];
  /** Suppresses the vistoria card's copy while its own read is in flight. */
  isVistoriasLoading: boolean;
  /** The vistoria read is known-flaky; a failure must not fake a status. */
  isVistoriasError: boolean;
  /** Injected in tests so the "current month" card is not clock-dependent. */
  today?: Date;
}

/**
 * The four cards above the tabs: this month's rent, last month's, the
 * entry inspection, and the contract itself [protótipo, Detalhe de Locação].
 *
 * Built locally rather than on the shared `KpiCard` because the design
 * paints the value in a per-card tone and puts a plain sentence under it,
 * while `KpiCard` fixes the value color and can only render its hint as a
 * delta badge. The registry list page still uses the shared card.
 *
 * @param props - The lease, its competências and its vistorias.
 * @returns The KPI row.
 */
export function LeaseDetailKpiCards({
  lease,
  expectations,
  vistorias,
  isVistoriasLoading,
  isVistoriasError,
  today = new Date(),
}: LeaseDetailKpiCardsProps) {
  const { t } = useTranslation('features/leases');
  const rentCents = findMonthlyRentCents(lease.charge_schedule);

  const currentPeriod = accrualPeriodOf(today);
  const current = findExpectation(expectations, currentPeriod);
  const previousPeriod = previousAccrualPeriod(currentPeriod);
  const previous = findExpectation(expectations, previousPeriod);

  const kpis: Kpi[] = [
    rentKpi(t, lease, rentCents),
    monthKpi(t, currentPeriod, current, 'kpiDetail.currentMonth'),
    monthKpi(t, previousPeriod, previous, 'kpiDetail.previousMonth'),
    entryVistoriaKpi(t, vistorias, isVistoriasLoading, isVistoriasError),
  ];

  return (
    <DetailStatStrip
      testId="lease-detail-kpis"
      items={kpis.map((kpi, index) => ({
        id: kpi.id,
        testId: `lease-kpi-${kpi.id}`,
        label: kpi.label,
        value: kpi.value,
        secondary: kpi.secondary,
        // The explaining sentence is hover-only now. Everything demoted here
        // restates what the value word already says ("Não gerada", "—"), or
        // repeats an amount the row beside it carries, so nothing a broker
        // must have is hidden behind a pointer.
        title: kpi.hint,
        accent: TONE_COLOR[kpi.tone],
        alignEnd: index === kpis.length - 1,
      }))}
    />
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * One month's rent card: whether it is paid, open, late or not yet raised.
 *
 * A period the generator has not reached yet is shown as "not generated"
 * rather than as paid -- an absent competência is not a settled one.
 */
function monthKpi(
  t: Translate,
  accrualPeriod: string,
  expectation: LeaseExpectation | null,
  labelKey: string,
): Kpi {
  const label = t(labelKey, { period: formatAccrualPeriod(accrualPeriod) });
  const id = labelKey.endsWith('currentMonth') ? 'current-month' : 'previous-month';

  if (!expectation) {
    return {
      id,
      label,
      value: t('kpiDetail.notGenerated'),
      hint: t('kpiDetail.notGeneratedHint'),
      tone: 'neutral',
    };
  }

  const { tone, daysOverdue } = resolveSituation(expectation);
  const amount = formatCents(expectation.expected_cents);

  const byTone: Record<string, { value: string; secondary?: string; hint: string; tone: Tone }> = {
    settled: {
      value: t('kpiDetail.paid'),
      secondary: formatCents(expectation.received_cents),
      hint: t('kpiDetail.paidHint', {
        amount: formatCents(expectation.received_cents),
        date: formatIsoDate(expectation.settled_on ?? expectation.due_date),
      }),
      tone: 'good',
    },
    writtenOff: {
      value: t('kpiDetail.writtenOff'),
      secondary: amount,
      hint: t('kpiDetail.writtenOffHint', { amount }),
      tone: 'neutral',
    },
    cancelled: {
      value: t('kpiDetail.cancelled'),
      secondary: amount,
      hint: t('kpiDetail.cancelledHint'),
      tone: 'neutral',
    },
    awaiting: {
      value: t('kpiDetail.awaiting'),
      secondary: amount,
      hint: t('kpiDetail.awaitingHint', { amount }),
      tone: 'warn',
    },
    overdue: {
      value: t('kpiDetail.overdue'),
      secondary: amount,
      hint: t('kpiDetail.overdueHint', {
        amount,
        date: formatIsoDate(expectation.due_date),
        count: daysOverdue,
      }),
      tone: 'bad',
    },
  };

  const resolved = byTone[tone] ?? {
    value: t('kpiDetail.open'),
    secondary: amount,
    hint: t('kpiDetail.openHint', { amount, date: formatIsoDate(expectation.due_date) }),
    tone: 'neutral' as Tone,
  };

  return { id, label, ...resolved };
}

/**
 * The tone for an entry vistoria's status: approved good, rejected bad, else warn.
 *
 * @param status - The vistoria status.
 * @returns The KPI card tone to paint it with.
 */
function vistoriaTone(status: string): Tone {
  if (status === 'approved') return 'good';
  if (status === 'rejected') return 'bad';
  return 'warn';
}

/**
 * The placeholder entry-vistoria card shown before the real status is known:
 * while the read is loading, when it failed, or when no move-in vistoria
 * exists. Returns null once a real vistoria is available.
 *
 * @param t - The `features/leases` translator.
 * @param base - The shared `{ id, label }` for the card.
 * @param entry - The move-in vistoria, if found.
 * @param flags - The loading/error state of the vistoria read.
 * @returns The placeholder KPI, or null when a real one should render.
 */
function entryVistoriaPlaceholder(
  t: Translate,
  base: { id: string; label: string },
  entry: EntryVistoria | undefined,
  flags: { isLoading: boolean; isError: boolean },
): Kpi | null {
  if (flags.isLoading) {
    return { ...base, value: '—', hint: t('kpiDetail.loading'), tone: 'neutral' };
  }
  if (flags.isError) {
    return { ...base, value: '—', hint: t('kpiDetail.vistoriaUnavailable'), tone: 'neutral' };
  }
  if (!entry) {
    return {
      ...base,
      value: t('kpiDetail.noEntryVistoria'),
      hint: t('kpiDetail.noEntryVistoriaHint'),
      tone: 'warn',
    };
  }
  return null;
}

/**
 * The entry inspection card, read from the lease's vistorias.
 *
 * A failed read says so instead of claiming the inspection is missing --
 * the two are very different facts to a broker.
 */
function entryVistoriaKpi(
  t: Translate,
  vistorias: EntryVistoria[],
  isLoading: boolean,
  isError: boolean,
): Kpi {
  const base = { id: 'entry-vistoria', label: t('kpiDetail.entryVistoria') };
  const entry = vistorias.find((v) => v.type === 'move_in');
  const placeholder = entryVistoriaPlaceholder(t, base, entry, { isLoading, isError });
  if (placeholder) return placeholder;

  return {
    ...base,
    value: t(`vistoriaStatus.${entry!.status}`, { defaultValue: entry!.status }),
    hint: t('kpiDetail.entryVistoriaHint', { count: entry!.photos?.length ?? 0 }),
    tone: vistoriaTone(entry!.status),
  };
}

/**
 * The monthly rent, leading the strip.
 *
 * The design asked for "vigente até <date>", but the lease model stores no
 * end date at all -- only `activated_at`. Rather than print a date the API
 * never sent, this states the rent and hangs when the vigência started off
 * it, and the missing term is reported as a backend gap.
 *
 * The testid stays `lease-kpi-contract` although the figure is now just the
 * rent: renaming it would drop a selector the tests rely on, which is a
 * separate sweep.
 */
function rentKpi(t: Translate, lease: Lease, rentCents: number | null): Kpi {
  return {
    id: 'contract',
    // `detail.monthlyRent` rather than `kpiDetail.contract`: the figure is
    // the rent, not the contract, and the key was freed up when the contract
    // summary stopped repeating this same number. The amount drops the
    // "/mês" suffix too, which the label now carries.
    label: t('detail.monthlyRent'),
    value: rentCents === null ? t('kpiDetail.rentUnknown') : formatCents(rentCents),
    hint: lease.activated_at
      ? t('kpiDetail.activatedAtHint', { date: formatIsoDate(lease.activated_at) })
      : t('kpiDetail.notActivatedHint'),
    tone: 'neutral',
  };
}

export default LeaseDetailKpiCards;
